import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
  useMediaQuery
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import { CrudActionEnum } from '@models/global.model.ts';
import TabsComponent from '@components/tabs/tabs.tsx';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import useProfileStore from '@store/profile-store/profile.store.ts';
import useLanguageStore from '@store/language-store/language.store.tsx';
import {
  convertToSelectedCategory,
  productsErrorMessages
} from '@modules/products/utils/products.utils.ts';
import { ProductsModel } from '@models/products.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import useProductsStore from '@modules/products/store/products.store.ts';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import ProductCategoriesDialog from '@modules/product-categories/components/product-categories-dialog/product-categories-dialog';
import { ProductCategoriesModel } from '@shared/models/product-categories.model';
import useProductCategoriesStore from '@modules/product-categories/store/product-categories.store';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import TimeEstimateInput from '@components/time-estimate-input/time-estimate-input.tsx';

const ProductsDialogContent: React.FC<{
  onSave: (data: ProductsModel) => void;
  updateProductData: ProductsModel | null;
}> = ({ onSave, updateProductData }) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { isLoading, category, getInventoryCategories } = useProductsStore();

  const {
    register,
    formState: { errors, isValid },
    control,
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<any>({ mode: 'onTouched' });

  const { selectedCurrency } = useProfileStore();
  const { languageList } = useLanguageStore();
  const { createProductCategories } = useProductCategoriesStore();

  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = useState<string>(defaultLang);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [updateProductCategories, setUpdateProductCategories] =
    useState<ProductCategoriesModel | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<any[]>([]);

  const [multiLangFields, setMultiLangFields] = useState<{
    name: Record<string, string | undefined>;
    description: Record<string, string | undefined>;
  }>({
    name: {},
    description: {}
  });
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const isTabletScreen = useMediaQuery('(max-width: 1715px)');

  useEffect(() => {
    if (updateProductData) {
      const convertedCategory = convertToSelectedCategory(updateProductData);
      setSelectedCategory(convertedCategory);
    } else {
      setSelectedCategory([]);
    }
  }, [updateProductData]);

  useEffect(() => {
    if (updateProductData) {
      const {
        name = {},
        description = {},
        unitPrice,
        deliveryTime
      } = updateProductData;

      setMultiLangFields({ name, description });
      reset({
        name: name[defaultLang] || '',
        description: description[defaultLang] || '',
        price: unitPrice,
        inventories: selectedCategory,
        deliveryTime
      });
    }
  }, [updateProductData, reset, defaultLang, selectedCategory]);

  useEffect(() => {
    if (selectedCategory.length > 0) {
      setValue('inventories', selectedCategory[0], { shouldValidate: true });
    }
  }, [selectedCategory, setValue]);

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentName = watch('name');
    const currentDescription = watch('description');

    setMultiLangFields((prev) => ({
      name: { ...prev.name, [currentTab]: currentName },
      description: { ...prev.description, [currentTab]: currentDescription }
    }));

    setCurrentTab(newLang);
    setValue('name', multiLangFields.name?.[newLang] || '');
    setValue('description', multiLangFields.description?.[newLang] || '');
  };

  const onSubmit = (data: any) => {
    const currentName = watch('name');
    const currentDescription = watch('description');

    const selectedInventory = Array.isArray(data.inventories)
      ? data.inventories[0]
      : data.inventories;

    const categoryId = selectedInventory?.id;

    const fullData: any = {
      categoryId,
      unitPrice: data.price,
      name: { ...multiLangFields.name, [currentTab]: currentName },
      description: {
        ...multiLangFields.description,
        [currentTab]: currentDescription
      },
      image: selectedMedia.image?.path || null
    };

    if (data.deliveryTime && data.deliveryTime.trim() !== '') {
      fullData.deliveryTime = data.deliveryTime;
    }

    onSave(fullData);
  };

  const handleFormSave = async (data: any) => {
    return new Promise(async (resolve) => {
      const res: any = await createProductCategories({
        data,
        handleClose: () => {
          setOpenDialog(false);
          setUpdateProductCategories(null);
        }
      });

      const newOption = {
        id: res?.data?.id || res?.data?._id,
        title: res?.data?.name
      };

      await getInventoryCategories();

      setSelectedCategory([newOption]);
      setValue('inventories', newOption, { shouldValidate: true });
      resolve(newOption);
    });
  };

  return (
    <div>
      <div className="w-full">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>

      <div className="mt-16 flex gap-14 max-lg-xl:mt-10">
        <div className="media-wrapper">
          <div className="flex mb-1.5">
            <span className="reuqired-symbole">*</span>
            {t('label_image')}
          </div>
          <MediaLibraryComponent
            mediaKey={'image'}
            allowedTypes={[FileTypeEnum.Image]}
            path={updateProductData?.image}
            onSelect={(media) =>
              setSelectedMedia((prev) => ({ ...prev, image: media }))
            }
          />
        </div>

        <form className="w-full flex flex-col gap-1">
          <FormControl error={!!errors.name}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_product_name')} <InfoTooltip text="tooltip_info_name" />
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              error={!!errors.name}
              placeholder={t('placeholder_name')}
            />
            <FormHelperText>
              {errors?.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages: productsErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <FormControl error={!!errors.price}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_package_price')}
              <InfoTooltip text="rooms_packages_management_package_price_tooltip" />
            </FormLabel>
            <OutlinedInput
              className="no-spinner"
              type="number"
              {...register('price', {
                required: true,
                valueAsNumber: true
              })}
              startAdornment={
                <InputAdornment position="start">
                  {selectedCurrency?.symbol}
                </InputAdornment>
              }
              placeholder={t('placeholder_price')}
            />
            <FormHelperText>
              {errors?.price &&
                getErrorMessage({
                  errors,
                  fieldName: 'price',
                  errorMessages: productsErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <TimeEstimateInput
            name="deliveryTime"
            label={
              <span className="flex items-center gap-1">
                {t('label_delivery_time')}
                <InfoTooltip text={t('tooltip_info_duration')} />
              </span>
            }
            placeholder={t('placeholder_delivery_time')}
            control={control}
            validationProps={{
              errors,
              errorMessages: productsErrorMessages,
              getErrorMessage,
              validationRules: {
                required: false
              }
            }}
          />

          <AppAutocompleteV2
            name="inventories"
            control={control}
            selectedList={selectedCategory}
            label={
              <span className="flex items-center">
                <span className="reuqired-symbole">*</span>
                {t('label_category')}
                <InfoTooltip text="tooltip_info_category" />
              </span>
            }
            placeholder={t('placeholder_category')}
            options={category}
            language={currentTab}
            addItemConfig={{
              action: CrudActionEnum.AddWithDialog,
              callback: (data: any) => {
                setOpenDialog(true);
                setUpdateProductCategories({ name: { en: data.title } });
              }
            }}
            validationProps={{
              errors,
              errorMessages: productsErrorMessages,
              getErrorMessage,
              validationRules: { required: true }
            }}
          />

          <FormControl error={!!errors.description}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_description')}
              <InfoTooltip text="tooltip_info_notes" />
            </FormLabel>
            <OutlinedInput
              multiline
              rows={5}
              {...register('description', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              placeholder={t('placeholder_notes')}
            />
            <FormHelperText>
              {errors?.description &&
                getErrorMessage({
                  errors,
                  fieldName: 'description',
                  errorMessages: productsErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <div className="flex justify-end pt-4 sticky pb-1 bottom-0 bg-(--white)">
            <Button
              variant="contained"
              loading={isLoading()}
              disabled={!isValid || !selectedMedia?.image}
              onClick={handleSubmit(onSubmit)}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </div>

      <AppDialog
        open={openDialog}
        size={isTabletScreen ? 'md' : 'lg'}
        title={t('product_categories_dialog_title')}
        content={
          <ProductCategoriesDialog
            updateProductCategoriesData={updateProductCategories}
            onSave={handleFormSave}
          />
        }
        onClose={() => setOpenDialog(false)}
      />
    </div>
  );
};

export default ProductsDialogContent;
